import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'default' | 'success' | 'warning' | 'destructive';

type ToastRecord = {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
};

type ToastInput = {
  title: string;
  description?: string;
  variant?: ToastVariant;
};

type ToastContextValue = {
  toast: (input: ToastInput) => void;
  dismiss: (id: string) => void;
  toasts: ToastRecord[];
};

const ToastContext = createContext<ToastContextValue | null>(null);

function variantStyles(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return 'border-emerald-500/40 bg-emerald-500/10';
    case 'warning':
      return 'border-amber-500/40 bg-amber-500/10';
    case 'destructive':
      return 'border-rose-500/40 bg-rose-500/10';
    default:
      return 'border-border bg-card/95';
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const context = useMemo<ToastContextValue>(
    () => ({
      toast: ({ title, description, variant = 'default' }) => {
        const id =
          typeof window !== 'undefined' && window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setToasts((current) => [...current, { id, title, description, variant }]);

        window.setTimeout(() => {
          setToasts((current) => current.filter((toast) => toast.id !== id));
        }, 3000);
      },
      dismiss: (id: string) => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
      },
      toasts
    }),
    [toasts]
  );

  return <ToastContext.Provider value={context}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside ToastProvider');
  }
  return context;
}

export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2">
      {toasts.map((toast) => (
        <article
          key={toast.id}
          className={cn(
            'pointer-events-auto rounded-lg border p-4 shadow-elevated backdrop-blur-sm animate-in-fade',
            variantStyles(toast.variant)
          )}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground">{toast.title}</h4>
              {toast.description ? <p className="mt-1 text-xs text-muted-foreground">{toast.description}</p> : null}
            </div>
            <button
              type="button"
              className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
              onClick={() => dismiss(toast.id)}
            >
              Dismiss
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
